import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Folder as FolderIcon,
  HourglassEmpty as ProcessingIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import type { S3FileItem } from '@/types/transferFile';
import { formatFileSize } from '@/utils/fileManagerUtils';
import FileActions from './FileActions';

export interface FileListProps {
  files: S3FileItem[];
  folders?: string[]; // Virtual folder names
  onDownload: (file: S3FileItem) => void;
  onDelete: (file: S3FileItem) => void;
  onFolderClick?: (folderName: string) => void; // Navigate to folder
  loading?: boolean;
  error?: string;
  loadingFile?: string;
}

const FileList: React.FC<FileListProps> = ({
  files,
  folders = [],
  onDownload,
  onDelete,
  onFolderClick,
  loading = false,
  error,
  loadingFile,
}) => {
  const formatDateSafe = (value: Date | string): string => {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return '-';
    return format(d, 'MMM dd, yyyy HH:mm');
  };

  const getFileTypeDisplay = (file: S3FileItem): string => {
    const extension = file.name.split('.').pop()?.toUpperCase();
    return extension || 'File';
  };

  const getFileTypeColor = (
    file: S3FileItem
  ): 'default' | 'primary' | 'secondary' => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'zip':
        return 'secondary';
      case 'csv':
        return 'primary';
      default:
        return 'default';
    }
  };

  const sortedFiles = [...files].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (files.length === 0 && folders.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No files or folders found in this location.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={1}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Size</TableCell>
            <TableCell>Last Modified</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Display folders first */}
          {folders.map(folderName => (
            <TableRow
              key={`folder-${folderName}`}
              hover
              onClick={() => onFolderClick?.(folderName)}
              sx={{
                cursor: onFolderClick ? 'pointer' : 'default',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderIcon color="primary" />
                  <Typography variant="body2" fontWeight="medium">
                    {folderName}
                  </Typography>
                </Box>
              </TableCell>

              <TableCell>
                <Chip
                  label="Folder"
                  size="small"
                  color="default"
                  variant="outlined"
                />
              </TableCell>

              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              </TableCell>

              <TableCell align="center">
                {/* No actions for folders */}
              </TableCell>
            </TableRow>
          ))}

          {/* Display files */}
          {sortedFiles.map(file => (
            <TableRow
              key={file.key}
              hover
              sx={{
                '&:hover': { backgroundColor: 'action.hover' },
                opacity: file.status === 'processing' ? 0.6 : 1,
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {file.status === 'processing' ? (
                    <ProcessingIcon color="warning" />
                  ) : (
                    <FileIcon color="action" />
                  )}
                  <Typography variant="body2">{file.name}</Typography>
                </Box>
              </TableCell>

              <TableCell>
                {file.status === 'processing' ? (
                  <Chip
                    label="Processing"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    label={getFileTypeDisplay(file)}
                    size="small"
                    color={getFileTypeColor(file)}
                    variant="outlined"
                  />
                )}
              </TableCell>

              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(file.size, { zeroDisplay: '-' })}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDateSafe(file.lastModified)}
                </Typography>
              </TableCell>

              <TableCell align="center">
                <FileActions
                  file={file}
                  onDownload={() => onDownload(file)}
                  onDelete={() => onDelete(file)}
                  loading={loadingFile === file.key}
                  disabled={file.status === 'processing'}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FileList;
